import { useMemo } from 'react';
import type { Booking, SlotOverride } from '@api/types';
import {
  HOURS,
  isoDate,
  fmtTime,
  eventStyle,
  overrideStyle,
  bookingTone,
} from './calendar-shared';

interface CalendarDayProps {
  date: Date;
  bookings: readonly Booking[];
  overrides: readonly SlotOverride[];
  onOpenBooking: (id: string) => void;
  onDeleteOverride: (id: string) => void;
}

export function CalendarDay({ date, bookings, overrides, onOpenBooking, onDeleteOverride }: CalendarDayProps) {
  const iso = isoDate(date);

  const dayBookings = useMemo(
    () => bookings.filter((b) => b.startTime.slice(0, 10) === iso),
    [bookings, iso],
  );

  const dayOverrides = useMemo(
    () => overrides.filter((ov) => {
      const dayStart = new Date(`${iso}T00:00:00.000Z`);
      const dayEnd   = new Date(`${iso}T23:59:59.999Z`);
      return new Date(ov.startTime) < dayEnd && new Date(ov.endTime) > dayStart;
    }),
    [overrides, iso],
  );

  return (
    <div className="cal-day" style={{ '--cal-cols': '1' } as React.CSSProperties}>
      <div className="cal-week-body">
        <div className="cal-time-col">
          {HOURS.map((h) => (
            <div key={h} className="cal-time-slot">
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        <div className="cal-day-col">
          {HOURS.map((h) => <div key={h} className="cal-grid-slot" />)}

          {dayOverrides.map((ov) => {
            const style = overrideStyle(ov.startTime, ov.endTime, iso);
            if (!style) return null;
            return (
              <div
                key={ov.id}
                className="cal-override"
                style={style}
                title={ov.reason ?? 'Bloqueado'}
              >
                <span className="cal-override-label">
                  {fmtTime(ov.startTime)}–{fmtTime(ov.endTime)}
                  {ov.reason ? ` · ${ov.reason}` : ''}
                </span>
                <button
                  type="button"
                  className="cal-override-close"
                  onClick={() => {
                    if (confirm('¿Eliminar este bloqueo?')) {
                      onDeleteOverride(ov.id);
                    }
                  }}
                  title="Eliminar bloqueo"
                >
                  ✕
                </button>
              </div>
            );
          })}

          {dayBookings.map((b) => (
            <button
              key={b.id}
              type="button"
              className="cal-event"
              data-tone={bookingTone(b.status)}
              style={{ ...eventStyle(b.startTime, b.endTime), cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
              title={`${b.customerName} · ${b.serviceName}`}
              onClick={() => { onOpenBooking(b.id); }}
            >
              <span className="cal-event-time">{fmtTime(b.startTime)} · {Math.round((new Date(b.endTime).getTime() - new Date(b.startTime).getTime()) / 60000)}m</span>
              <span className="cal-event-title">{b.serviceName}</span>
              <span className="cal-event-sub">{b.customerName} {b.staffName ? `· ${b.staffName}` : ''}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
